import { uniqBy } from 'lodash'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleCloseIcon } from '@icons/CircleCloseIcon'
import { MoneyIcon } from '@icons/MoneyIcon'
import { PercentIcon } from '@icons/PercentIcon'

import { debounce } from '@utils/debounce'
import { useWindowResize } from '@utils/domUtils'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { NameDto } from '@model/NameDto'

import { Badge } from '@global/badge/Badge'
import { Button } from '@global/button/Button'
import { FilterFields } from '@global/filter/FilterFields'
import { FilterQueryProps, FilterQueryPropsValue } from '@global/filter/FilterQueryModal'

import { FilterChipDropdownList } from './Chips/FilterChipDropdownList'
import { DateShortcutOption, FilterChipModalDateRange } from './Chips/FilterChipModalDateRange'
import { FilterChipModalRange } from './Chips/FilterChipModalRange'
import { FilterHorizontalScroll } from './FilterHorizontalScroll'

import styles from './TableFilter.module.scss'

interface BadgeData {
  name: string
  value: string | number
  type: FilterOptions
}

export enum FilterOptions {
  ClientVolume,
  Amount,
  TradingAccountCount,
  SignedUpDate,
  VolumeFxm,
  VolumeOther,
  AccumulatedVolumeFxm,
  AccumulatedVolumeOther,
  TotalCommission,
  AccumulatedCommission,
  RebateAmount,
  TransactionDate,
  IBRebateApprovalStatus = 'ca_search_Status',
  RebateApprovalType = 'ca_search_RebateApprovalType',
  RebatePeriod = 'ca_search_RebatePeriod',
  Country = 'ca_search_Country',
  TradingAccountId = 'ca_search_TradingAccountId',
  ClientId = 'ca_search_ClientId',
  IntroducingBrokerGroupId = 'ca_search_IntroducingBrokerGroupId',
  HasTradingAccounts = 'ca_search_HasTradingAccounts',
  TradingAccountType = 'ca_search_TradingAccountType',
  RebateGroupId = 'ca_search_RebateGroupId',
  IBRebateGroupId = 'ca_search_GroupId',
  Status = 'ca_search_State',
}

interface SearchableEntityProps {
  options?: NameDto<string>[]
  setSearch?: (value?: FilterQueryProps) => void
}

type TableFilterProps = {
  isVisible?: boolean
  approvalTypes?: NameDto[]
  rebatePeriods?: NameDto[]
  statusOptions?: NameDto[]
  tradingAccountTypeOptions?: NameDto[]
  groupSearchProps?: SearchableEntityProps
  tradingAccountSearchProps?: SearchableEntityProps
  clientsSearchProps?: SearchableEntityProps
  countryOptions?: NameDto<string>[]
  dateShortcutOptions?: DateShortcutOption[]
  searchFilters?: FilterQueryProps
  filterOptions: FilterOptions[]
  customLabels?: { [key in FilterOptions]?: string }
  setSearchFilters: (value?: FilterQueryProps) => void
  showRebateCheckbox?: boolean
}

export const TableFilter: FC<TableFilterProps> = (props) => {
  const {
    setSearchFilters,
    filterOptions,
    searchFilters,
    countryOptions,
    approvalTypes,
    statusOptions,
    rebatePeriods,
    groupSearchProps,
    tradingAccountSearchProps,
    isVisible,
    clientsSearchProps,
    tradingAccountTypeOptions,
    showRebateCheckbox,
  } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const {
    activeSearchableFilterItems,
    onActiveSearchableFilterItemsChange,
    setActiveSearchableFilterItems,
  } = useActiveSearchableFilterItems({
    groupSearchProps,
    tradingAccountSearchProps,
    clientsSearchProps,
  })

  const activeFilterItems = useMemo<BadgeData[]>(() => {
    if (!searchFilters) {
      return []
    }

    return filterOptions.reduce<BadgeData[]>((activeFilters, option) => {
      let filterData: BadgeData[] = []

      if (option === FilterOptions.Country) {
        filterData = getSingleSelectNameDtoFilter(option, searchFilters[option], countryOptions)
      } else if (option === FilterOptions.ClientVolume) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.QueryVolumeFrom,
          to: FilterFields.QueryVolumeTo,
        })
      } else if (option === FilterOptions.Amount) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.AmountFrom,
          to: FilterFields.AmountTo,
        })
      } else if (option === FilterOptions.TradingAccountCount) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.TradingAccountsCountFrom,
          to: FilterFields.TradingAccountsCountTo,
        })
      } else if (option === FilterOptions.VolumeFxm) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.VolumeFxmFrom,
          to: FilterFields.VolumeFxmTo,
        })
      } else if (option === FilterOptions.VolumeOther) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.VolumeOtherFrom,
          to: FilterFields.VolumeOtherTo,
        })
      } else if (option === FilterOptions.AccumulatedVolumeOther) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.AccumulatedVolumeOtherFrom,
          to: FilterFields.AccumulatedVolumeOtherTo,
        })
      } else if (option === FilterOptions.AccumulatedVolumeFxm) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.AccumulatedVolumeFxmFrom,
          to: FilterFields.AccumulatedVolumeFxmTo,
        })
      } else if (option === FilterOptions.TotalCommission) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.TotalCommissionFrom,
          to: FilterFields.TotalCommissionTo,
        })
      } else if (option === FilterOptions.AccumulatedCommission) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.AccumulatedCommissionFrom,
          to: FilterFields.AccumulatedCommissionTo,
        })
      } else if (option === FilterOptions.RebateAmount) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.RebateAmountFrom,
          to: FilterFields.RebateAmountTo,
        })
        if (showRebateCheckbox && searchFilters[FilterFields.HasRebate] === 'false') {
          filterData = [
            {
              name: t('IB.RebatesPage.Rebate N/A'),
              value: 'false',
              type: option,
            },
          ]
        }
      } else if (option === FilterOptions.SignedUpDate) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.SignedUpDateFrom,
          to: FilterFields.SignedUpDateTo,
        })
      } else if (option === FilterOptions.RebateApprovalType) {
        filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], approvalTypes)
      } else if (option === FilterOptions.TradingAccountType) {
        filterData = getMultiSelectNameDtoFilter(
          option,
          searchFilters[option],
          tradingAccountTypeOptions
        )
      } else if (option === FilterOptions.IBRebateApprovalStatus) {
        filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], statusOptions)
      } else if (option === FilterOptions.RebatePeriod) {
        filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], rebatePeriods)
      } else if (option === FilterOptions.IntroducingBrokerGroupId) {
        filterData = optionsToBadges(activeSearchableFilterItems?.[option] || [], option)
      } else if (
        option === FilterOptions.RebateGroupId ||
        option === FilterOptions.IBRebateGroupId
      ) {
        filterData = optionsToBadges(activeSearchableFilterItems?.[option] || [], option)
      } else if (option === FilterOptions.TradingAccountId) {
        filterData = optionsToBadges(activeSearchableFilterItems?.[option] || [], option)
      } else if (option === FilterOptions.ClientId) {
        filterData = optionsToBadges(activeSearchableFilterItems?.[option] || [], option)
      } else if (option === FilterOptions.Status) {
        filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], statusOptions)
      } else if (option === FilterOptions.TransactionDate) {
        filterData = getClientVolumeFilter(option, searchFilters, {
          from: FilterFields.TransactionDateFrom,
          to: FilterFields.TransactionDateTo,
        })
      }

      return [...activeFilters, ...filterData]
    }, [])
  }, [
    searchFilters,
    filterOptions,
    countryOptions,
    approvalTypes,
    statusOptions,
    rebatePeriods,
    activeSearchableFilterItems,
    tradingAccountTypeOptions,
    showRebateCheckbox,
    t,
  ])

  const handleSetSearchFilters = (value?: FilterQueryProps) => {
    onActiveSearchableFilterItemsChange(value)

    setSearchFilters(value)
  }

  const handleSingleFilterClear = (badgeData: BadgeData) => {
    if (badgeData.type === FilterOptions.Country) {
      handleSetSearchFilters({ ...searchFilters, [badgeData.type]: undefined })
    } else if (badgeData.type === FilterOptions.RebateApprovalType) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.TradingAccountType) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.IBRebateApprovalStatus) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.RebatePeriod) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.ClientVolume) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.QueryVolumeFrom]: undefined,
        [FilterFields.QueryVolumeTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.Amount) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.AmountFrom]: undefined,
        [FilterFields.AmountTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.TradingAccountCount) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.TradingAccountsCountFrom]: undefined,
        [FilterFields.TradingAccountsCountTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.VolumeFxm) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.VolumeFxmFrom]: undefined,
        [FilterFields.VolumeFxmTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.VolumeOther) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.VolumeOtherFrom]: undefined,
        [FilterFields.VolumeOtherTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.AccumulatedVolumeOther) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.AccumulatedVolumeOtherFrom]: undefined,
        [FilterFields.AccumulatedVolumeOtherTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.AccumulatedVolumeFxm) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.AccumulatedVolumeFxmFrom]: undefined,
        [FilterFields.AccumulatedVolumeFxmTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.TotalCommission) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.TotalCommissionFrom]: undefined,
        [FilterFields.TotalCommissionTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.AccumulatedCommission) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.AccumulatedCommissionFrom]: undefined,
        [FilterFields.AccumulatedCommissionTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.RebateAmount) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.RebateAmountFrom]: undefined,
        [FilterFields.RebateAmountTo]: undefined,
      })
      if (showRebateCheckbox) {
        handleSetSearchFilters({
          ...searchFilters,
          [FilterFields.HasRebate]: undefined,
        })
      }
    } else if (badgeData.type === FilterOptions.SignedUpDate) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.SignedUpDateFrom]: undefined,
        [FilterFields.SignedUpDateTo]: undefined,
      })
    } else if (badgeData.type === FilterOptions.IntroducingBrokerGroupId) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (
      badgeData.type === FilterOptions.RebateGroupId ||
      badgeData.type === FilterOptions.IBRebateGroupId
    ) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.TradingAccountId) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.ClientId) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.Status) {
      handleSetSearchFilters({
        ...searchFilters,
        [badgeData.type]: removeMultiFilterValue(badgeData, searchFilters),
      })
    } else if (badgeData.type === FilterOptions.TransactionDate) {
      handleSetSearchFilters({
        ...searchFilters,
        [FilterFields.TransactionDateFrom]: undefined,
        [FilterFields.TransactionDateTo]: undefined,
      })
    }
  }

  const handleClear = () => {
    handleSetSearchFilters({})
    setActiveSearchableFilterItems(undefined)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className={styles.tableFilter}>
      {isMobile ? (
        <div className={styles.badgesArea}>
          <FilterHorizontalScroll>
            {activeFilterItems.map((filterItem) => {
              return (
                <Badge.New
                  key={`${filterItem.type}-${filterItem.value}`}
                  text={filterItem.name}
                  rightIcon={CircleCloseIcon}
                  color='blue'
                  onRightIconClick={() => handleSingleFilterClear(filterItem)}
                />
              )
            })}
            {filterOptions?.map((filterOption) => {
              return (
                <ChipByOption
                  key={filterOption}
                  {...props}
                  filterOption={filterOption}
                  setSearchFilters={handleSetSearchFilters}
                />
              )
            })}
            <Button.New onClick={handleClear} appearance='secondary' size='XS'>
              {t('Clear Filters')}
            </Button.New>
          </FilterHorizontalScroll>
        </div>
      ) : (
        <>
          <div className={styles.badgesArea}>
            <FilterHorizontalScroll>
              {filterOptions?.map((filterOption) => (
                <ChipByOption
                  key={filterOption}
                  {...props}
                  filterOption={filterOption}
                  setSearchFilters={handleSetSearchFilters}
                />
              ))}
            </FilterHorizontalScroll>
          </div>
          <div className={styles.clearButton}>
            <Button.New onClick={handleClear} appearance='secondary' size='XS'>
              {t('Clear Filters')}
            </Button.New>
          </div>
        </>
      )}
    </div>
  )
}

interface FilterChipByTypeProps extends TableFilterProps {
  filterOption: FilterOptions
}

const ChipByOption: FC<FilterChipByTypeProps> = (props) => {
  const {
    filterOption,
    customLabels,
    countryOptions = [],
    searchFilters,
    approvalTypes,
    tradingAccountTypeOptions,
    rebatePeriods,
    statusOptions,
    tradingAccountSearchProps,
    groupSearchProps,
    clientsSearchProps,
    dateShortcutOptions,
    setSearchFilters,
    showRebateCheckbox,
  } = props
  const { t } = useTranslation()

  switch (filterOption) {
    case FilterOptions.RebateApprovalType:
      return (
        <FilterChipDropdownList
          name={
            customLabels?.[FilterOptions.RebateApprovalType] ||
            t('IB.RebatesPage.GroupsPage.Group type')
          }
          filterType={FilterOptions.RebateApprovalType}
          options={approvalTypes}
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          isMultiSelect
        />
      )
    case FilterOptions.TradingAccountType:
      return (
        <FilterChipDropdownList
          name={
            customLabels?.[FilterOptions.TradingAccountType] || t('Trading Account.Account type')
          }
          filterType={FilterOptions.TradingAccountType}
          options={tradingAccountTypeOptions}
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          isMultiSelect
        />
      )
    case FilterOptions.IBRebateApprovalStatus:
    case FilterOptions.Status:
      return (
        <FilterChipDropdownList
          name={customLabels?.[filterOption] || t('Status')}
          filterType={filterOption}
          options={statusOptions}
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          isMultiSelect
        />
      )
    case FilterOptions.TransactionDate:
      return (
        <FilterChipModalDateRange
          name={customLabels?.[FilterOptions.TransactionDate] || t('Date')}
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.TransactionDateFrom,
            to: FilterFields.TransactionDateTo,
          }}
          shortcutOptions={dateShortcutOptions}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.RebatePeriod:
      return (
        <FilterChipDropdownList
          name={
            customLabels?.[FilterOptions.RebatePeriod] ||
            t('IB.RebatesPage.GroupsPage.Rebate period')
          }
          filterType={FilterOptions.RebatePeriod}
          options={rebatePeriods}
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          isMultiSelect
        />
      )
    case FilterOptions.Amount:
      return (
        <FilterChipModalRange
          name={customLabels?.[FilterOptions.Amount] || t('IB.RebatesPage.GroupsPage.Rebate')}
          onSelect={(value) => setSearchFilters(value)}
          inputIcon={PercentIcon}
          keys={{
            from: FilterFields.AmountFrom,
            to: FilterFields.AmountTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.TradingAccountCount:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.TradingAccountCount] ||
            t('IB.RebatesPage.GroupsPage.TAs count')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.TradingAccountsCountFrom,
            to: FilterFields.TradingAccountsCountTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.VolumeFxm:
      return (
        <FilterChipModalRange
          name={customLabels?.[FilterOptions.VolumeFxm] || t('IB.RebatesPage.ClientsPage.Vol. FXM')}
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.VolumeFxmFrom,
            to: FilterFields.VolumeFxmTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.VolumeOther:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.VolumeOther] || t('IB.RebatesPage.ClientsPage.Vol. Other')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.VolumeOtherFrom,
            to: FilterFields.VolumeOtherTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.AccumulatedVolumeFxm:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.AccumulatedVolumeFxm] ||
            t('IB.RebatesPage.ClientsPage.Vol. FXM')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.AccumulatedVolumeFxmFrom,
            to: FilterFields.AccumulatedVolumeFxmTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.AccumulatedVolumeOther:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.AccumulatedVolumeOther] ||
            t('IB.RebatesPage.ClientsPage.Vol. Other')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.AccumulatedVolumeOtherFrom,
            to: FilterFields.AccumulatedVolumeOtherTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.TotalCommission:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.TotalCommission] ||
            t('IB.RebatesPage.ClientsPage.Commission')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.TotalCommissionFrom,
            to: FilterFields.TotalCommissionTo,
          }}
          state={searchFilters}
          inputIcon={MoneyIcon}
          isQueryParameter
        />
      )
    case FilterOptions.AccumulatedCommission:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.AccumulatedCommission] ||
            t('IB.RebatesPage.ClientsPage.Commission')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.AccumulatedCommissionFrom,
            to: FilterFields.AccumulatedCommissionTo,
          }}
          state={searchFilters}
          inputIcon={MoneyIcon}
          isQueryParameter
        />
      )
    case FilterOptions.RebateAmount:
      return (
        <FilterChipModalRange
          name={
            customLabels?.[FilterOptions.RebateAmount] || t('IB.RebatesPage.ClientsPage.Rebate')
          }
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.RebateAmountFrom,
            to: FilterFields.RebateAmountTo,
          }}
          {...(showRebateCheckbox && {
            checkBox: {
              key: FilterFields.HasRebate,
              label: t('IB.RebatesPage.Rebate N/A'),
              checkedValue: 'false',
              uncheckedValue: undefined,
            },
          })}
          state={searchFilters}
          isQueryParameter
          inputIcon={PercentIcon}
        />
      )
    case FilterOptions.SignedUpDate:
      return (
        <FilterChipModalDateRange
          name={customLabels?.[FilterOptions.SignedUpDate] || t('IB.RebatesPage.Signed up on')}
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.SignedUpDateFrom,
            to: FilterFields.SignedUpDateTo,
          }}
          shortcutOptions={dateShortcutOptions}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.Country:
      return (
        <FilterChipDropdownList
          name={customLabels?.[FilterOptions.Country] || t('Country')}
          filterType={FilterOptions.Country}
          options={countryOptions}
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          hasSearch={countryOptions.length > 6}
          isMultiSelect
        />
      )
    case FilterOptions.ClientVolume:
      return (
        <FilterChipModalRange
          name={customLabels?.[FilterOptions.ClientVolume] || t('IB.Volume')}
          onSelect={(value) => setSearchFilters(value)}
          keys={{
            from: FilterFields.QueryVolumeFrom,
            to: FilterFields.QueryVolumeTo,
          }}
          state={searchFilters}
          isQueryParameter
        />
      )
    case FilterOptions.IntroducingBrokerGroupId:
    case FilterOptions.RebateGroupId:
    case FilterOptions.IBRebateGroupId:
      return (
        <FilterChipDropdownList
          name={customLabels?.[filterOption] || t('IB.RebatesPage.GroupsPage.Group name')}
          filterType={filterOption}
          options={groupSearchProps?.options}
          onSearchChange={
            groupSearchProps?.setSearch
              ? debounce(
                  (value) => groupSearchProps?.setSearch?.({ [FilterFields.Name]: value }),
                  300
                )
              : undefined
          }
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          hasSearch
          isMultiSelect
        />
      )
    case FilterOptions.TradingAccountId:
      return (
        <FilterChipDropdownList
          name={
            customLabels?.[FilterOptions.TradingAccountId] ||
            t('IB.RebatesPage.ClientsPage.Trading account')
          }
          filterType={FilterOptions.TradingAccountId}
          options={tradingAccountSearchProps?.options}
          onSearchChange={
            tradingAccountSearchProps?.setSearch
              ? debounce(
                  (value) =>
                    tradingAccountSearchProps?.setSearch?.({
                      [FilterFields.TradingAccountName]: value,
                    }),
                  300
                )
              : undefined
          }
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          hasSearch
          isMultiSelect
        />
      )
    case FilterOptions.ClientId:
      return (
        <FilterChipDropdownList
          name={customLabels?.[FilterOptions.ClientId] || t('IB.Client Name')}
          filterType={FilterOptions.ClientId}
          options={clientsSearchProps?.options}
          onSearchChange={
            clientsSearchProps?.setSearch
              ? debounce(
                  (value) => clientsSearchProps?.setSearch?.({ [FilterFields.Name]: value }),
                  300
                )
              : undefined
          }
          onSelect={(value) => setSearchFilters(value)}
          state={searchFilters}
          hasSearch
          isMultiSelect
        />
      )
    default:
      return null
  }
}

type FilterQueryOptionValue = NameDto<string | number>[] | undefined

interface ActiveSearchableFilterItemsParams {
  groupSearchProps?: SearchableEntityProps
  tradingAccountSearchProps?: SearchableEntityProps
  clientsSearchProps?: SearchableEntityProps
}

interface ActiveSearchableFilterItemsValue {
  activeSearchableFilterItems?: Partial<Record<FilterOptions, FilterQueryOptionValue>>
  setActiveSearchableFilterItems: (
    value?: Partial<Record<FilterOptions, FilterQueryOptionValue>>
  ) => void
  onActiveSearchableFilterItemsChange: (value?: FilterQueryProps) => void
}

const useActiveSearchableFilterItems = (
  props: ActiveSearchableFilterItemsParams
): ActiveSearchableFilterItemsValue => {
  const { groupSearchProps, tradingAccountSearchProps, clientsSearchProps } = props

  const [activeSearchableFilterItems, setActiveSearchableFilterItems] =
    useState<Partial<Record<FilterOptions, FilterQueryOptionValue>>>()

  const onActiveSearchableFilterItemsChange = (value?: FilterQueryProps) => {
    const updateFilterItems = (
      filterOption: FilterOptions,
      ids: string[] | undefined,
      options: NameDto<string | number>[] | undefined
    ) => {
      setActiveSearchableFilterItems((prevState) => {
        if (!ids?.length) {
          return { ...prevState, [filterOption]: undefined }
        }

        const prevOptions = prevState?.[filterOption] || []
        return {
          ...prevState,
          [filterOption]: uniqBy([...prevOptions, ...(options || [])], 'id').filter((option) =>
            ids.includes(option.id.toString())
          ),
        }
      })
    }

    updateFilterItems(
      FilterOptions.IntroducingBrokerGroupId,
      (value?.[FilterOptions.IntroducingBrokerGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )

    updateFilterItems(
      FilterOptions.RebateGroupId,
      (value?.[FilterOptions.RebateGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )

    updateFilterItems(
      FilterOptions.IBRebateGroupId,
      (value?.[FilterOptions.IBRebateGroupId] as string)?.split(' '),
      groupSearchProps?.options
    )

    updateFilterItems(
      FilterOptions.IBRebateApprovalStatus,
      (value?.[FilterOptions.IBRebateApprovalStatus] as string)?.split(' '),
      groupSearchProps?.options
    )

    updateFilterItems(
      FilterOptions.TradingAccountId,
      (value?.[FilterOptions.TradingAccountId] as string)?.split(' '),
      tradingAccountSearchProps?.options
    )

    updateFilterItems(
      FilterOptions.ClientId,
      (value?.[FilterOptions.ClientId] as string)?.split(' '),
      clientsSearchProps?.options
    )
  }

  return {
    activeSearchableFilterItems,
    setActiveSearchableFilterItems,
    onActiveSearchableFilterItemsChange,
  }
}

const getFromToLabel = (from?: string, to?: string): string => {
  if (from?.length && to?.length) {
    return `${from} - ${to}`
  }

  if (from?.length || to?.length) {
    return `${from ?? to}`
  }

  return ''
}

const optionsToBadges = (
  options: NameDto<string | number>[],
  filterType: FilterOptions
): BadgeData[] => {
  return options.map((option) => ({
    name: option.name,
    value: option.id,
    type: filterType,
  }))
}

const getMultiSelectNameDtoFilter = (
  option: FilterOptions,
  filterValue: FilterQueryPropsValue,
  options?: NameDto<string | number>[]
): BadgeData[] => {
  if (!filterValue || typeof filterValue !== 'string') {
    return []
  }

  const filterValues = filterValue.trim().split(' ')

  const matchedOptions = options?.filter((option) => filterValues.includes(option.id.toString()))

  if (!matchedOptions?.length) {
    return []
  }

  return optionsToBadges(matchedOptions, option)
}

const getSingleSelectNameDtoFilter = (
  option: FilterOptions,
  filterValue: FilterQueryPropsValue,
  countries?: NameDto<string>[]
): BadgeData[] => {
  if (!filterValue) {
    return []
  }

  const country = countries?.find((country) => country.id === filterValue)

  if (!country) {
    return []
  }

  return [
    {
      name: country.name,
      value: filterValue as string,
      type: option,
    },
  ]
}

const getClientVolumeFilter = (
  option: FilterOptions,
  searchFilters: FilterQueryProps,
  keys: { from: FilterFields; to: FilterFields }
): BadgeData[] => {
  const label = getFromToLabel(
    extractValueFromFilterQuery(searchFilters[keys.from]),
    extractValueFromFilterQuery(searchFilters[keys.to])
  )

  if (!label.length) {
    return []
  }

  return [
    {
      name: label,
      value: label,
      type: option,
    },
  ]
}

const removeMultiFilterValue = (badgeData: BadgeData, searchFilters?: FilterQueryProps) => {
  const filterValue = searchFilters?.[badgeData.type]

  if (!filterValue || typeof filterValue !== 'string') {
    return
  }

  const filterValues = filterValue.split(' ')

  return filterValues.filter((value) => value !== badgeData.value.toString()).join(' ')
}
